import React, { useState, useEffect, useMemo } from "react";
import { Search, Users } from "lucide-react";
import { useGetAllOrgUsersForFilterQuery } from "../../../store/apis/user.api";
import Modal2, { Modal2Footer, Modal2Button } from "../../common/Modal2";
import Input from "../../common/Input";
import RadioButton from "../../common/RadioButton";
import { IApprovalGroup } from "../../../store/apis/dynamicWorkflow.api";
import { TextArea } from "../../common/TextArea";

export interface Employee {
  id: string;
  name: string;
  designation: string;
  department: string;
}

export interface ApprovalGroup {
  id?: string | undefined;
  name: string;
  description: string;
  status: "active" | "inactive";
  memberIds: string[];
  groupType?: string;
  isSystemGroup?: boolean;
}

interface GroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: ApprovalGroup | null;
  onSave: (data: IApprovalGroup) => void;
  isLoading: boolean;
  statusOption: { value: string; label: string }[];
}

export function GroupDialog({
  open,
  onOpenChange,
  initialData,
  onSave,
  isLoading,
  statusOption,
}: GroupDialogProps) {
  const { data: employees, isLoading: isLoadingEmployees } =
    useGetAllOrgUsersForFilterQuery();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<string>("");
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [employeeSearch, setEmployeeSearch] = useState("");

  const isEditing = !!initialData;

  useEffect(() => {
    if (open) {
      if (initialData) {
        setName(initialData.name);
        setDescription(initialData.description);
        setStatus(initialData.status);
        setSelectedMembers(initialData.memberIds);
      } else {
        setName("");
        setDescription("");
        setStatus("");
        setSelectedMembers([]);
      }
      setEmployeeSearch("");
    }
  }, [open, initialData]);

  const filteredEmployees = useMemo(() => {
    return employees?.filter((emp) =>
      emp.fullName.toLowerCase().includes(employeeSearch.toLowerCase())
    );
  }, [employees, employeeSearch]);

  const toggleMember = (id: string) => {
    setSelectedMembers((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || selectedMembers.length === 0 || !status) return;
    onSave({ name, description, status, memberIds: selectedMembers });
  };

  const formId = "group-form";

  return (
    <Modal2
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title={isEditing ? "Edit Group" : "New Group"}
      subtitle={
        isEditing ? "Update group details" : "Create a reusable people group"
      }
      width={520}
      maskClosable={false}
      loading={isLoading}
      footer={
        <Modal2Footer>
          <Modal2Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Modal2Button>
          <Modal2Button
            variant="primary"
            type="submit"
            form={formId}
            loading={isLoading}
            disabled={!name || selectedMembers.length === 0 || status === "" || isLoading}
          >
            {isEditing ? "Update Group" : "Create Group"}
          </Modal2Button>
        </Modal2Footer>
      }
    >
      <form id={formId} onSubmit={handleSave} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Input
            label="Group Name"
            required
            value={name}
            placeholder="Enter group name"
            onChange={(val) => setName(String(val))}
          />
          <div className="flex flex-col gap-1.5">
            <RadioButton
              label="Status"
              options={
                statusOption?.length > 0
                  ? statusOption
                  : [
                      { label: "Active", value: "true" },
                      { label: "Inactive", value: "false" },
                    ]
              }
              disabled={statusOption?.length === 0}
              value={status}
              onChange={(v) => setStatus(String(v ?? ""))}
              name="status"
              required
            />
          </div>
        </div>

        <TextArea
          label="Group Description"
          value={description}
          placeholder="Enter group description"
          onChange={(val: string) => setDescription(val)}
          minRows={2}
        />

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-[13.5px] font-bold text-[var(--color-text-primary)] flex items-center gap-2">
              <Users className="w-4 h-4" />
              Group Members <span className="text-[var(--color-error-dark)]">*</span>
            </h3>
            <span className="text-xs text-[var(--color-text-secondary)] bg-[var(--color-bg-muted)] px-2 py-1 rounded-full">
              {selectedMembers.length} Selected
            </span>
          </div>

          <Input
            placeholder="Search employees by name"
            value={employeeSearch}
            onChange={(val) => setEmployeeSearch(String(val))}
            leftIcon={<Search className="w-4 h-4 text-[var(--color-text-tertiary)]" />}
          />

          <div className="border border-[var(--color-border-secondary)] rounded-[13px] h-60 overflow-y-auto bg-[var(--color-bg-base)] divide-y divide-[var(--color-border-tertiary)]">
            {isLoadingEmployees ? (
              <div className="p-4 text-center text-[13px] text-[var(--color-text-tertiary)]">
                Loading employees...
              </div>
            ) : filteredEmployees?.length === 0 ? (
              <div className="p-4 text-center text-[13px] text-[var(--color-text-tertiary)]">
                No employees found.
              </div>
            ) : (
              filteredEmployees?.map((emp) => (
                <label
                  key={emp.id}
                  className="flex items-center gap-3 p-3 hover:bg-[var(--color-bg-muted)] cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedMembers.includes(emp.id)}
                    onChange={() => toggleMember(emp.id)}
                    className="w-4 h-4 text-[var(--color-primary-600)] rounded border-[var(--color-border-primary)] focus:ring-[var(--color-primary-500)]"
                  />
                  <div className="flex items-center gap-2">
                    <p className="text-[13px] font-semibold text-[var(--color-text-primary)] mb-0">
                      {emp.fullName}
                    </p>
                    <span className="text-[var(--color-text-tertiary)]">•</span>
                    <p className="text-xs text-[var(--color-text-secondary)] mb-0">
                      {emp.empCode}
                    </p>
                  </div>
                </label>
              ))
            )}
          </div>
        </div>
      </form>
    </Modal2>
  );
}
